import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit2, Trash2, Star, X, Mail, CheckCircle2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PrevNextNav from '@/components/PrevNextNav';
import { usePrevNextNav } from '@/hooks/usePrevNextNav';
import SousTraitantForm from '@/components/forms/SousTraitantForm';
import ContractsPanel from '@/components/ContractsPanel';
import SubcontractorDocumentsPanel from '@/components/SubcontractorDocumentsPanel';
import TarifsPanel from '@/components/TarifsPanel';
import EvaluationsPanel from '@/components/EvaluationsPanel';
import CandidaturePanel from '@/components/CandidaturePanel';
import LastTouchedBadge from '@/components/LastTouchedBadge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function SousTraitantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sousTraitant, setSousTraitant] = useState(null);
  const [chantiers, setChantiers] = useState({ en_cours: [], prevus: [], termines: [] });
  const [affectations, setAffectations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showAffectation, setShowAffectation] = useState(false);
  const [selectedChantier, setSelectedChantier] = useState('');
  const [chantiersDisponibles, setChantiersDisponibles] = useState([]);
  const [docsSummary, setDocsSummary] = useState([]);
  const nav = usePrevNextNav({
    storageKey: 'sous_traitants_list_order',
    currentId: id,
    routePrefix: '/sous-traitants',
  });

  const changeStatut = async (newStatut) => {
    try {
      await api.patch(`/sous-traitants/${id}/statut`, { statut: newStatut });
      toast.success('Statut mis à jour');
      setSousTraitant(prev => ({ ...prev, statut_recrutement: newStatut }));
    } catch (e) {
      toast.error('Échec mise à jour statut');
    }
  };

  const resendForm = async () => {
    try {
      await api.post(`/sous-traitants/${id}/send-onboarding-form`).catch(async () => {
        // Fallback : simple email marker (l'endpoint réel peut varier)
        await api.patch(`/sous-traitants/${id}/statut`, {
          statut: 'en_attente_docs', note: 'Formulaire renvoyé',
        });
      });
      toast.success('Formulaire renvoyé au sous-traitant');
      setSousTraitant(prev => ({ ...prev, statut_recrutement: 'en_attente_docs' }));
    } catch (e) {
      toast.error("Échec renvoi du formulaire");
    }
  };

  useEffect(() => {
    fetchSousTraitant();
    fetchChantiersST();
    fetchChantiersDisponibles();
    fetchAffectations();
    fetchDocsSummary();
  }, [id]);

  const fetchDocsSummary = async () => {
    try {
      const r = await api.get(`/sous-traitants/${id}/documents`);
      setDocsSummary(r.data.documents || []);
    } catch (_e) {
      setDocsSummary([]);
    }
  };

  const fetchAffectations = async () => {
    try {
      const response = await api.get('/affectations');
      setAffectations(response.data.filter(a => a.sous_traitant_id === id));
    } catch (error) {
      console.error('Erreur chargement affectations:', error);
    }
  };

  const getAffectationId = (chantierId) => {
    const aff = affectations.find(a => a.chantier_id === chantierId);
    return aff ? aff.affectation_id : null;
  };

  const handleDesaffectation = async (chantierId) => {
    const affectationId = getAffectationId(chantierId);
    if (!affectationId) {
      toast.error('Affectation non trouvée');
      return;
    }
    if (!window.confirm('Voulez-vous vraiment retirer ce sous-traitant de ce chantier ?')) return;
    try {
      await api.delete(`/affectations/${affectationId}`);
      toast.success('Sous-traitant retiré du chantier');
      fetchChantiersST();
      fetchAffectations();
    } catch (error) {
      console.error('Erreur désaffectation:', error);
      toast.error('Erreur lors de la désaffectation');
    }
  };

  const fetchSousTraitant = async () => {
    try {
      const response = await api.get(`/sous-traitants/${id}`);
      setSousTraitant(response.data);
      document.title = `${response.data.nom} - Reno Resolve`;
      setLoading(false);
    } catch (error) {
      console.error('Erreur chargement sous-traitant:', error);
      toast.error('Sous-traitant non trouvé');
      setLoading(false);
      setTimeout(() => navigate('/sous-traitants'), 2000);
    }
  };

  const fetchChantiersST = async () => {
    try {
      const response = await api.get(`/sous-traitants/${id}/chantiers`);
      setChantiers(response.data);
    } catch (error) {
      console.error('Erreur chargement chantiers ST:', error);
    }
  };

  const fetchChantiersDisponibles = async () => {
    try {
      const response = await api.get('/chantiers');
      setChantiersDisponibles(response.data.filter(c => c.statut !== 'Terminé'));
    } catch (error) {
      console.error('Erreur chargement chantiers:', error);
    }
  };

  const handleAffectation = async () => {
    if (!selectedChantier) {
      toast.error('Veuillez sélectionner un chantier');
      return;
    }

    try {
      await api.post('/affectations', {
        chantier_id: selectedChantier,
        sous_traitant_id: id,
        mission: 'À définir',
        statut: 'Prévu'
      });
      toast.success('Sous-traitant affecté au chantier');
      setShowAffectation(false);
      setSelectedChantier('');
      fetchChantiersST();
      fetchAffectations();
    } catch (error) {
      console.error('Erreur affectation:', error);
      toast.error('Erreur lors de l\'affectation');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Voulez-vous vraiment supprimer ce sous-traitant ?')) return;
    
    try {
      await api.delete(`/sous-traitants/${id}`);
      toast.success('Sous-traitant supprimé');
      navigate('/sous-traitants');
    } catch (error) {
      console.error('Erreur suppression sous-traitant:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSTUpdated = () => {
    setShowEdit(false);
    // Différer les state updates au tick suivant pour laisser Radix/Portal
    // terminer proprement l'unmount du Dialog (Select interne) et éviter
    // l'erreur "removeChild: node is not a child of this node".
    setTimeout(() => {
      fetchSousTraitant();
      fetchDocsSummary();
    }, 0);
    // NB : le toast de succès est déjà émis par SousTraitantForm — pas de doublon ici.
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/sous-traitants')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {sousTraitant.nom}
            </h1>
            <p className="text-slate-600 font-mono text-sm">{sousTraitant.sous_traitant_id}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <PrevNextNav {...nav} className="mr-2" />

          <Select value={sousTraitant.statut_recrutement || 'prospect'} onValueChange={changeStatut}>
            <SelectTrigger className="w-40 h-9" data-testid="st-statut-select">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="prospect">🟣 Prospect</SelectItem>
              <SelectItem value="relance">🟠 Relancé</SelectItem>
              <SelectItem value="en_attente_docs">🟡 En attente docs</SelectItem>
              <SelectItem value="actif">🟢 Actif</SelectItem>
              <SelectItem value="inactif">⚪ Inactif</SelectItem>
            </SelectContent>
          </Select>

          {['prospect', 'relance', 'en_attente_docs'].includes(sousTraitant.statut_recrutement) && (
            <Button variant="outline" onClick={resendForm} data-testid="st-resend-form-btn"
              className="border-amber-300 text-amber-700 hover:bg-amber-50">
              <Mail className="w-4 h-4 mr-2" />
              Renvoyer le formulaire
            </Button>
          )}

          {sousTraitant.statut_recrutement !== 'actif' && (
            <Button onClick={() => changeStatut('actif')}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="st-activate-btn">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Rendre actif
            </Button>
          )}

          <Button variant="outline" onClick={() => setShowEdit(true)} data-testid="edit-st-btn">
            <Edit2 className="w-4 h-4 mr-2" />
            Modifier
          </Button>
          <Button variant="destructive" onClick={handleDelete} data-testid="delete-st-btn">
            <Trash2 className="w-4 h-4 mr-2" />
            Supprimer
          </Button>
        </div>
      </div>

      {/* Panneau d'état de candidature — n'apparaît que pour prospect/en_attente_docs/en_examen */}
      <CandidaturePanel stId={id} onStatusChanged={fetchSousTraitant} />

      {/* Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Informations Générales
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-500">Spécialité principale</p>
                <Badge className="mt-1">{sousTraitant.specialite}</Badge>
              </div>
              {sousTraitant.zone && (
                <div>
                  <p className="text-sm text-slate-500">Zone d'Intervention</p>
                  <p className="text-slate-900 mt-1">{sousTraitant.zone}</p>
                </div>
              )}
              {(sousTraitant.specialites_secondaires || []).length > 0 && (
                <div className="col-span-2">
                  <p className="text-sm text-slate-500 mb-1.5">Autres corps de métier</p>
                  <div className="flex flex-wrap gap-1.5">
                    {sousTraitant.specialites_secondaires.map((s) => (
                      <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <p className="text-sm text-slate-500">Téléphone</p>
                <p className="text-slate-900 mt-1">{sousTraitant.telephone}</p>
              </div>
              {sousTraitant.email && (
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="text-slate-900 mt-1">{sousTraitant.email}</p>
                </div>
              )}
              {sousTraitant.prix_range && (
                <div className="col-span-2">
                  <p className="text-sm text-slate-500 mb-2">Niveau de Prix</p>
                  <div className="text-2xl font-semibold text-amber-600">
                    {sousTraitant.prix_range}
                  </div>
                </div>
              )}
              {sousTraitant.tarif_journalier && (
                <div>
                  <p className="text-sm text-slate-500">Tarif Journalier</p>
                  <p className="text-xl font-bold text-blue-600 mt-1">
                    {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(sousTraitant.tarif_journalier)}/jour
                  </p>
                </div>
              )}
              {sousTraitant.siret && (
                <div>
                  <p className="text-sm text-slate-500">SIRET</p>
                  <p className="text-slate-900 font-mono mt-1">{sousTraitant.siret}</p>
                </div>
              )}
              {sousTraitant.certifications && (
                <div className="col-span-2">
                  <p className="text-sm text-slate-500">Certifications</p>
                  <p className="text-slate-900 mt-1">{sousTraitant.certifications}</p>
                </div>
              )}

              {/* Documents légaux — résumé condensé avec badges colorés */}
              {docsSummary.length > 0 && (
                <div className="col-span-2 pt-2 border-t border-slate-100">
                  <p className="text-sm text-slate-500 mb-2">Documents légaux</p>
                  <div className="flex flex-wrap gap-2">
                    {docsSummary.map((d) => {
                      // Vert = valide, orange = expire <15j, rouge = expiré, gris = manquant
                      const cfg =
                        d.status === 'ok'      ? { bg: 'bg-green-50',   ring: 'border-green-200',   text: 'text-green-700',   label: d.expires_at ? `Valide · exp. ${new Date(d.expires_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}` : 'Valide' } :
                        d.status === 'warning' ? { bg: 'bg-orange-50',  ring: 'border-orange-200',  text: 'text-orange-700',  label: `Expire dans ${d.days_left} j` } :
                        d.status === 'expired' ? { bg: 'bg-red-50',     ring: 'border-red-200',     text: 'text-red-700',     label: `Expiré depuis ${Math.abs(d.days_left || 0)} j` } :
                                                 { bg: 'bg-slate-50',   ring: 'border-slate-200',   text: 'text-slate-500',   label: 'Non transmis' };
                      return (
                        <div
                          key={d.type}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${cfg.bg} ${cfg.ring} ${cfg.text} text-xs`}
                          data-testid={`st-doc-summary-${d.type}`}
                        >
                          <span className="font-semibold">{d.label}</span>
                          <span>·</span>
                          <span>{cfg.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {sousTraitant.note_qualite && (
                <div className="col-span-2">
                  <p className="text-sm text-slate-500 mb-2">Note Qualité</p>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-5 h-5 ${
                            i < Math.floor(sousTraitant.note_qualite)
                              ? 'text-amber-400 fill-amber-400'
                              : 'text-slate-300'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-lg font-semibold text-slate-900">
                      {sousTraitant.note_qualite}/5
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Bloc Tarifs — modifiable inline, plusieurs options tarifaires */}
          <TarifsPanel sousTraitant={sousTraitant} onSaved={fetchSousTraitant} />

          {/* Bloc Notation interne — moyennes par critère + historique */}
          <EvaluationsPanel
            sousTraitantId={id}
            allChantiers={chantiers}
            onSaved={fetchSousTraitant}
          />

          {sousTraitant.notes && (
            <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Notes
              </h2>
              <p className="text-slate-700 whitespace-pre-wrap">{sousTraitant.notes}</p>
            </div>
          )}

          {/* Section Chantiers */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Chantiers
            </h2>
            
            {/* En cours */}
            <div className="mb-6">
              <h3 className="text-md font-semibold text-green-700 mb-2 flex items-center gap-2">
                <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                En Cours ({chantiers.en_cours.length})
              </h3>
              {chantiers.en_cours.length > 0 ? (
                <div className="space-y-2">
                  {chantiers.en_cours.map((c) => (
                    <div
                      key={c.chantier_id}
                      className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                    >
                      <div
                        onClick={() => navigate(`/chantiers/${c.chantier_id}`)}
                        className="flex-1 cursor-pointer hover:opacity-80"
                      >
                        <p className="font-medium text-slate-900">{c.ville}</p>
                        <p className="text-sm text-slate-500 font-mono">{c.chantier_id}</p>
                        {(c.nom || c.project_title) && (
                          <p className="text-xs text-slate-800 font-medium">{c.nom || c.project_title}</p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDesaffectation(c.chantier_id)}
                        data-testid={`desaffecter-${c.chantier_id}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">Aucun chantier en cours</p>
              )}
            </div>

            {/* Prévisionnels */}
            <div className="mb-6">
              <h3 className="text-md font-semibold text-blue-700 mb-2 flex items-center gap-2">
                <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                Prévisionnels ({chantiers.prevus.length})
              </h3>
              {chantiers.prevus.length > 0 ? (
                <div className="space-y-2">
                  {chantiers.prevus.map((c) => (
                    <div
                      key={c.chantier_id}
                      className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg"
                    >
                      <div
                        onClick={() => navigate(`/chantiers/${c.chantier_id}`)}
                        className="flex-1 cursor-pointer hover:opacity-80"
                      >
                        <p className="font-medium text-slate-900">{c.ville}</p>
                        <p className="text-sm text-slate-500 font-mono">{c.chantier_id}</p>
                        {(c.nom || c.project_title) && (
                          <p className="text-xs text-slate-800 font-medium">{c.nom || c.project_title}</p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDesaffectation(c.chantier_id)}
                        data-testid={`desaffecter-${c.chantier_id}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">Aucun chantier prévisionnel</p>
              )}
            </div>

            {/* Terminés */}
            <div>
              <h3 className="text-md font-semibold text-slate-700 mb-2 flex items-center gap-2">
                <span className="w-3 h-3 bg-slate-500 rounded-full"></span>
                Terminés ({chantiers.termines.length})
              </h3>
              {chantiers.termines.length > 0 ? (
                <div className="space-y-2">
                  {chantiers.termines.map((c) => (
                    <div
                      key={c.chantier_id}
                      className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg"
                    >
                      <div
                        onClick={() => navigate(`/chantiers/${c.chantier_id}`)}
                        className="flex-1 cursor-pointer hover:opacity-80"
                      >
                        <p className="font-medium text-slate-900">{c.ville}</p>
                        <p className="text-sm text-slate-500 font-mono">{c.chantier_id}</p>
                        {(c.nom || c.project_title) && (
                          <p className="text-xs text-slate-800 font-medium">{c.nom || c.project_title}</p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDesaffectation(c.chantier_id)}
                        data-testid={`desaffecter-${c.chantier_id}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">Aucun chantier terminé</p>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
            <h3 className="font-semibold text-slate-900 mb-3">Informations</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Date d'ajout</span>
                <span className="text-slate-900 font-medium">
                  {format(new Date(sousTraitant.date_creation), 'dd/MM/yyyy', { locale: fr })}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-6 border border-blue-100">
            <h3 className="font-semibold text-blue-900 mb-2">Actions Rapides</h3>
            <div className="space-y-2">
              <Button 
                className="w-full" 
                variant="outline"
                onClick={() => setShowAffectation(true)}
                data-testid="affecter-chantier-btn"
              >
                Affecter à un Chantier
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Contracts panel — generate & e-sign subcontractor contracts */}
      <div className="max-w-7xl mx-auto px-6 pb-8 space-y-6">
        <ContractsPanel
          sousTraitantId={id}
          sousTraitant={sousTraitant}
          onSousTraitantUpdated={fetchSousTraitant}
        />
        <SubcontractorDocumentsPanel sousTraitantId={id} onRefresh={fetchDocsSummary} />
        <LastTouchedBadge entityType="sous-traitants" entityId={id} />
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>Modifier le Sous-traitant</DialogTitle>
          </DialogHeader>
          <SousTraitantForm
            sousTraitant={sousTraitant}
            onSuccess={handleSTUpdated}
            onCancel={() => setShowEdit(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Affectation Dialog */}
      <Dialog open={showAffectation} onOpenChange={setShowAffectation}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Affecter à un Chantier</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Sélectionner un chantier</Label>
              <Select value={selectedChantier} onValueChange={setSelectedChantier}>
                <SelectTrigger>
                  <SelectValue placeholder="Choisir un chantier..." />
                </SelectTrigger>
                <SelectContent>
                  {chantiersDisponibles.map((chantier) => (
                    <SelectItem key={chantier.chantier_id} value={chantier.chantier_id}>
                      {chantier.ville} - {chantier.chantier_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowAffectation(false)}>
                Annuler
              </Button>
              <Button onClick={handleAffectation}>
                Affecter
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}